import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar add to cart button
 * @property {HTMLButtonElement} [buyNowButton] - Sticky bar buy now button
 * @property {HTMLButtonElement} [minusButton] - Sticky quantity minus
 * @property {HTMLButtonElement} [plusButton] - Sticky quantity plus
 * @property {HTMLElement} quantityNumber - Sticky quantity display
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #openDrawerAfterAdd = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#syncBuyNowVisibility();
    window.setTimeout(() => this.#syncBuyNowVisibility(), 500);
    window.setTimeout(() => this.#syncBuyNowVisibility(), 2000);

    const productForm = this.#getProductForm();
    if (productForm) {
      const checkoutObserver = new MutationObserver(() => this.#syncBuyNowVisibility());
      checkoutObserver.observe(productForm, { childList: true, subtree: true });
      this.#abortController.signal.addEventListener('abort', () => checkoutObserver.disconnect());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  handleAddToCartClick = async () => {
    const mainButton = this.#targetAddToCartButton;
    const stickyButton = this.refs.addToCartButton;
    if (!mainButton || !stickyButton) return;

    const mainAddToCartComponent = mainButton.closest('add-to-cart-component');

    mainButton.dataset.puppet = 'true';
    mainButton.click();

    await Promise.all([
      mainAddToCartComponent?.animateAddToCart
        ? mainAddToCartComponent.animateAddToCart()
        : this.#playAddToCartAnimation(mainButton),
      this.#playAddToCartAnimation(stickyButton),
    ]);

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      stickyButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * @param {HTMLButtonElement} button
   */
  #playAddToCartAnimation = async (button) => {
    if (button.dataset.added !== 'true') {
      button.dataset.added = 'true';
    }

    await onAnimationEnd(button);
  };

  handleBuyNowClick = () => {
    this.#openDrawerAfterAdd = true;
    this.handleAddToCartClick();
    window.setTimeout(() => {
      this.#openDrawerAfterAdd = false;
    }, 8000);
  };

  handleQuantityDecrease = () => {
    const minusButton = this.#getMainQuantityButton('minus');
    minusButton?.click();
    this.#syncQuantityFromMain();
  };

  handleQuantityIncrease = () => {
    const plusButton = this.#getMainQuantityButton('plus');
    plusButton?.click();
    this.#syncQuantityFromMain();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#syncQuantityFromMain();
    this.#syncBuyNowVisibility();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
      this.#resetTimeout = undefined;
    }

    this.refs.addToCartButton.removeAttribute('data-added');

    if (!this.#openDrawerAfterAdd) return;
    this.#openDrawerAfterAdd = false;
    if (event.type === ThemeEvents.cartError) return;
    this.#openCartDrawer();
  };

  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      return;
    }

    const icon = document.querySelector('.header-actions__cart-icon');
    const trigger = icon?.closest('button, a');
    if (trigger instanceof HTMLElement) trigger.click();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateQuantityDisplay();
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * @param {'minus' | 'plus'} name
   * @returns {HTMLButtonElement | null}
   */
  #getMainQuantityButton(name) {
    const productForm = this.#getProductForm();
    if (!productForm) return null;

    return productForm.querySelector(`quantity-selector-component button[name="${name}"]`);
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getMainQuantityInput() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;

    return productForm.querySelector('quantity-selector-component input[name="quantity"]');
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getPaymentButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;

    return productForm.querySelector(
      '.shopify-payment-button__button--unbranded, .shopify-payment-button__button'
    );
  }

  #syncBuyNowVisibility() {
    const { buyNowButton } = this.refs;
    if (!(buyNowButton instanceof HTMLButtonElement)) return;

    const paymentButton = this.#getPaymentButton();
    if (paymentButton && !paymentButton.disabled) {
      buyNowButton.hidden = false;
    } else {
      buyNowButton.hidden = true;
    }
  }

  #getInitialQuantity() {
    this.#syncQuantityFromMain();
  }

  #syncQuantityFromMain() {
    const quantityInput = this.#getMainQuantityInput();
    if (quantityInput) {
      this.#currentQuantity = parseInt(quantityInput.value, 10) || 1;
    } else {
      this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    }
    this.#updateQuantityDisplay();
  }

  #updateQuantityDisplay() {
    const { quantityNumber, minusButton, plusButton, addToCartButton, buyNowButton } = this.refs;
    quantityNumber.textContent = this.#currentQuantity.toString();

    const available = this.dataset.variantAvailable === 'true';
    const mainPlusButton = this.#getMainQuantityButton('plus');
    const mainMinusButton = this.#getMainQuantityButton('minus');

    if (minusButton) {
      minusButton.disabled = !available || Boolean(mainMinusButton?.disabled);
    }
    if (plusButton) {
      plusButton.disabled = !available || Boolean(mainPlusButton?.disabled);
    }
    if (addToCartButton) addToCartButton.disabled = !available;
    if (buyNowButton) buyNowButton.disabled = !available;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
